import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuItem from '@mui/material/MenuItem';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/EditRounded';
import { PopperMenu } from '@components/Popper';
import { Checkbox } from '@components/Checkbox';
import Button from '@components/Button';
import { MenuTinyArrow } from '@components/MenuTinyArrow';
import { Typography } from '@components/Typography';
import { handleApiError } from '@helpers/api';
import { AssignedStatus } from '@constants/conversations';
import { useTranslation, trackExport, userCoversTeam } from '@hooks';
import { alertActions, conversationsActions } from '@actions';
import { conversationsService } from '@services';
import EditBulkModal from './EditBulkModal';
import { ConversationsToolbar } from './styles';

const SelectedDialogsToolbar = ({ selectedConversations, setSelectedConversations, counters, filter }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);

    const conversations = useSelector(state => state.conversations?.data || []);
    const currentUser = useSelector(state => state.auth.user);

    const dispatch = useDispatch();
    const { t } = useTranslation();

    const handleOpenEl = event => setAnchorEl(event.currentTarget);

    const handleCloseEl = () => setAnchorEl(null);

    const onSelectAll = () => {
        setSelectedConversations(prev => {
            const allSelected = prev.has('all');
            return allSelected ? new Map() : new Map([['all', true]]);
        });
    };

    const exportAs = async format => {
        try {
            const response = await conversationsService.exportCSV(
                {
                    selectedConversations: [...selectedConversations.keys()].filter(id => id !== 'all'),
                    selectByFilter: selectedConversations.has('all'),
                    filter,
                },
                format,
            );
            if (response?.exportStatusId) {
                trackExport(response.exportStatusId);
                dispatch(
                    alertActions.success(t('exports.exporting'), { id: response.exportStatusId, persistent: true }),
                );
            }
            setSelectedConversations(new Map());
        } catch (error) {
            handleApiError(error);
        } finally {
            handleCloseEl();
        }
    };

    const onEditBulk = async values => {
        // INFO: values - property, status
        try {
            const result = await conversationsService.editBulk({
                selectedConversations: [...selectedConversations.keys()].filter(id => id !== 'all'),
                selectByFilter: selectedConversations.has('all'),
                filter,
                ...values,
            });
            dispatch(conversationsActions.getCounters(filter));
            dispatch(conversationsActions.get(filter));
            // Server edits only own-team rows; blame a shortfall on collaborators only for explicit selections (select-all shortfall can be concurrent drift).
            const updated = result?.updated;
            const shortfall =
                typeof updated === 'number' && typeof selectedCount === 'number' && updated < selectedCount;
            dispatch(
                alertActions.success(
                    shortfall && readOnlySelectedCount > 0
                        ? t('InboxPage.bulkEditPartial', { updated, total: selectedCount })
                        : shortfall
                          ? t('InboxPage.bulkEditUpdatedCount', { updated, total: selectedCount })
                          : t('alertMessages.editSuccess'),
                ),
            );
            setSelectedConversations(new Map());
        } catch (error) {
            handleApiError(error);
        } finally {
            handleCloseEl();
            setEditDialogOpen(false);
        }
    };

    const CounterMap = {
        [AssignedStatus.Mine]: counters?.assigned,
        [AssignedStatus.Unassigned]: counters?.unassigned,
        [AssignedStatus.All]: counters?.all,
        [AssignedStatus.Assigned]: counters?.filtered,
    };

    const allSelected = selectedConversations.has('all');
    const selectedCount = allSelected ? CounterMap[filter.assignment] : selectedConversations.size;

    const explicitIds = [...selectedConversations.keys()].filter(id => id !== 'all');
    const readOnlySelectedCount = allSelected
        ? 0
        : explicitIds.filter(id => {
              const row = conversations.find(c => c.id === id);
              return row && !userCoversTeam(currentUser, row.teamId);
          }).length;
    const selectionIsReadOnly = !allSelected && explicitIds.length > 0 && readOnlySelectedCount === explicitIds.length;

    return (
        <ConversationsToolbar>
            <Typography
                variant="caption"
                sx={{
                    color: 'var(--primary-main)',
                    display: 'flex',
                    alignItems: 'center',
                    width: 'fit-content',
                    cursor: 'pointer',
                    overflow: 'hidden',
                }}
                onClick={onSelectAll}
            >
                <Checkbox checked={allSelected} indeterminate={!allSelected} />
                <span style={{ marginLeft: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t('InboxPage.conversationSelected', { count: selectedCount })}
                </span>
            </Typography>
            {anchorEl && (
                <ClickAwayListener onClickAway={handleCloseEl}>
                    <PopperMenu anchorEl={anchorEl} open={!!anchorEl}>
                        {/* Server skips cross-team rows in bulk edits — hide the action when it would no-op */}
                        {!filter.collaboratingOnly && !selectionIsReadOnly && (
                            <MenuItem onClick={() => setEditDialogOpen(true)}>
                                <EditIcon
                                    sx={{
                                        height: '18px',
                                        width: '18px',
                                        color: 'var(--text-secondary)',
                                        marginRight: '8px',
                                    }}
                                />
                                <Typography variant="body2">{t('InboxPage.editConversations')}</Typography>
                            </MenuItem>
                        )}
                        <MenuItem onClick={() => exportAs('csv')}>
                            <FileDownloadIcon
                                sx={{
                                    height: '18px',
                                    width: '18px',
                                    color: 'var(--text-secondary)',
                                    marginRight: '8px',
                                }}
                            />
                            <Typography variant="body2">{t('buttons.exportAsCSV')}</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => exportAs('xlsx')}>
                            <FileDownloadIcon
                                sx={{
                                    height: '18px',
                                    width: '18px',
                                    color: 'var(--text-secondary)',
                                    marginRight: '8px',
                                }}
                            />
                            <Typography variant="body2">{t('buttons.exportAsExcel')}</Typography>
                        </MenuItem>
                    </PopperMenu>
                </ClickAwayListener>
            )}
            <Button
                variant="text"
                sx={{
                    fontSize: '0.875rem',
                    lineHeight: '16px',
                    letterSpacing: '0.1px',
                    height: '24px',
                    padding: '4px',
                    minWidth: 'initial',
                }}
                onClick={handleOpenEl}
            >
                {t('common.actions')}
                <MenuTinyArrow isUp={!!anchorEl} />
            </Button>
            {editDialogOpen ? (
                <EditBulkModal
                    open={editDialogOpen}
                    setOpen={setEditDialogOpen}
                    selectedCount={selectedCount}
                    onSubmit={onEditBulk}
                />
            ) : null}
        </ConversationsToolbar>
    );
};

export default SelectedDialogsToolbar;
